import type {FilterBarSelect, SelectOption} from '@/components/ui';
import type {GameHostFilters, AccountFilters, OrganizationFilters} from '@/types';
import {US_STATES} from '@/constants';

/**
 * Single source of truth for the dropdown filters on every admin list surface.
 * Each builder returns the FilterBar config for one entity from the account-type
 * labels + live option data, so the Hosts / Accounts / Organizations views (and
 * the account-scoped host list) all expose the same complete, consistent set —
 * by construction, in both Academic and Business labelings.
 */
export interface ListFilterLabels {
    hostSingular: string;
    hostPlural: string;
    accountSingular: string;
    accountPlural: string;
    organizationSingular: string;
    organizationPlural: string;
}

const STATUS_OPTIONS: SelectOption[] = [
    {value: '', label: 'All Statuses'},
    {value: 'active', label: 'Active'},
    {value: 'inactive', label: 'Inactive'},
];

const stateOptions = (): SelectOption[] => [
    {value: '', label: 'All States'},
    ...US_STATES.map(s => ({value: s, label: s})),
];

export function buildHostFilters(opts: {
    labels: ListFilterLabels;
    filters: GameHostFilters;
    onChange: (patch: Partial<GameHostFilters>) => void;
    organizationOptions: SelectOption[];
    accountOptions: SelectOption[];
    // Account-scoped surfaces (e.g. Account Detail) already fix the account, so
    // the organization/account reference dropdowns are redundant there.
    includeReferenceFilters?: boolean;
}): FilterBarSelect[] {
    const {labels, filters, onChange, organizationOptions, accountOptions, includeReferenceFilters = true} = opts;

    const accessFilter: FilterBarSelect = {
        value: filters.access || '',
        onChange: v => onChange({access: (v || undefined) as GameHostFilters['access']}),
        placeholder: 'Filter by access',
        options: [
            {value: '', label: 'All Access'},
            {value: 'active', label: 'Active'},
            {value: 'host_inactive', label: `${labels.hostSingular} inactive`},
            {value: 'account_inactive', label: `${labels.accountSingular} inactive`},
            {value: 'account_expired', label: `${labels.accountSingular} expired`},
            {value: 'org_inactive', label: `${labels.organizationSingular} inactive`},
            {value: 'org_expired', label: `${labels.organizationSingular} expired`},
        ],
    };
    const organizationFilter: FilterBarSelect = {
        value: filters.organization_id || '',
        onChange: v => onChange({organization_id: v || undefined}),
        placeholder: `Filter by ${labels.organizationSingular.toLowerCase()}`,
        options: [{value: '', label: `All ${labels.organizationPlural}`}, ...organizationOptions],
    };
    const accountFilter: FilterBarSelect = {
        value: filters.account_id || '',
        onChange: v => onChange({account_id: v || undefined}),
        placeholder: `Filter by ${labels.accountSingular.toLowerCase()}`,
        options: [{value: '', label: `All ${labels.accountPlural}`}, ...accountOptions],
    };
    const invitationFilter: FilterBarSelect = {
        value: filters.invitation_status || '',
        onChange: v => onChange({invitation_status: (v || undefined) as GameHostFilters['invitation_status']}),
        placeholder: 'Filter by registration status',
        options: [
            {value: '', label: 'All Registration Statuses'},
            {value: 'accepted', label: 'Registered'},
            {value: 'sent', label: 'Invitation Sent'},
            {value: 'pending', label: 'Pending'},
            {value: 'expired', label: 'Expired'},
        ],
    };
    const statusFilter: FilterBarSelect = {
        value: filters.status || '',
        onChange: v => onChange({status: (v || undefined) as GameHostFilters['status']}),
        placeholder: 'Filter by status',
        options: STATUS_OPTIONS,
    };

    return includeReferenceFilters
        ? [accessFilter, organizationFilter, accountFilter, invitationFilter, statusFilter]
        : [accessFilter, invitationFilter, statusFilter];
}

export function buildAccountFilters(opts: {
    labels: ListFilterLabels;
    filters: AccountFilters;
    onChange: (patch: Partial<AccountFilters>) => void;
    organizationOptions: SelectOption[];
}): FilterBarSelect[] {
    const {labels, filters, onChange, organizationOptions} = opts;
    return [
        {
            value: filters.organization_id || '',
            onChange: v => onChange({organization_id: v || undefined}),
            placeholder: `Filter by ${labels.organizationSingular.toLowerCase()}`,
            options: [{value: '', label: `All ${labels.organizationPlural}`}, ...organizationOptions],
        },
        {
            value: filters.state || '',
            onChange: v => onChange({state: v || undefined}),
            placeholder: 'Filter by state',
            options: stateOptions(),
        },
        {
            value: filters.status || '',
            onChange: v => onChange({status: (v || undefined) as AccountFilters['status']}),
            placeholder: 'Filter by status',
            options: STATUS_OPTIONS,
        },
    ];
}

export function buildOrganizationFilters(opts: {
    labels: ListFilterLabels;
    filters: OrganizationFilters;
    onChange: (patch: Partial<OrganizationFilters>) => void;
    // Org list is filtered by a chosen child account (School / Tier 2); that
    // selection lives in its own state (an account id), separate from filters.
    schoolFilter: string;
    onSchoolFilterChange: (value: string) => void;
    accountOptions: SelectOption[];
}): FilterBarSelect[] {
    const {labels, filters, onChange, schoolFilter, onSchoolFilterChange, accountOptions} = opts;
    return [
        {
            value: schoolFilter,
            onChange: onSchoolFilterChange,
            placeholder: `Filter by ${labels.accountSingular.toLowerCase()}`,
            options: [{value: '', label: `All ${labels.accountPlural}`}, ...accountOptions],
        },
        {
            value: filters.state || '',
            onChange: v => onChange({state: v || undefined}),
            placeholder: 'Filter by state',
            options: stateOptions(),
        },
        {
            value: filters.status || '',
            onChange: v => onChange({status: (v || undefined) as OrganizationFilters['status']}),
            placeholder: 'Filter by status',
            options: STATUS_OPTIONS,
        },
    ];
}
